/**
 * 单条 AI 调用记录卡片（旧 components/common/LLMLogCard.vue）：
 * 请求 ID、时间、用户、供应商与用量，Token 详情 / 请求数据 / 响应数据默认折叠
 */
import { App, Button, Card, Col, Collapse, Row, Space, Statistic, Tag, Typography } from 'antd'
import {
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudOutlined,
  CopyOutlined,
  DollarOutlined,
  SendOutlined,
  TagOutlined,
  UserOutlined
} from '@ant-design/icons'
import { formatDateTime } from '@/utils/datetime'
import type { LLMLog } from './types'
import styles from './LLMLogCard.module.css'

// 供应商标签颜色（antd 预设色名）
const PROVIDER_COLORS: Record<string, string> = {
  openai: 'green',
  anthropic: 'blue',
  claude: 'blue',
  google: 'orange',
  gemini: 'orange',
  xai: 'purple',
  cerebras: 'cyan',
  deepseek: 'magenta'
}

export function providerColor(provider?: string | null) {
  if (!provider) return 'default'
  return PROVIDER_COLORS[provider.toLowerCase()] ?? 'default'
}

export function formatJSON(data: unknown) {
  if (!data) return '无数据'
  try {
    return JSON.stringify(data, null, 2)
  } catch {
    // 循环引用等无法序列化的数据
    return '（数据无法格式化）'
  }
}

interface LLMLogCardProps {
  log: LLMLog
  // 全局记录页显示调用用户，用户记录页不显示
  showUserInfo?: boolean
}

export function LLMLogCard({ log, showUserInfo = false }: Readonly<LLMLogCardProps>) {
  const { message } = App.useApp()
  const hasTokenInfo = !!(log.model || log.provider || log.total_tokens)

  const copyRequestId = async () => {
    try {
      await navigator.clipboard.writeText(log.request_id)
      message.success('请求ID已复制到剪贴板')
    } catch (error) {
      console.error('复制失败:', error)
      message.error('复制失败')
    }
  }

  const jsonPanel = (data: unknown) => <pre className={styles.json}>{formatJSON(data)}</pre>
  const sections = [
    ...(hasTokenInfo
      ? [
          {
            key: 'tokens',
            label: (
              <span>
                <BarChartOutlined /> Token详情
              </span>
            ),
            children: (
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="输入 Tokens" value={log.prompt_tokens ?? 0} />
                </Col>
                <Col span={6}>
                  <Statistic title="输出 Tokens" value={log.completion_tokens ?? 0} />
                </Col>
                <Col span={6}>
                  <Statistic title="总计 Tokens" value={log.total_tokens ?? 0} />
                </Col>
                <Col span={6}>
                  <Statistic title="总成本" value={log.total_cost ?? 0} precision={6} suffix="USD" />
                </Col>
              </Row>
            )
          }
        ]
      : []),
    {
      key: 'request',
      label: (
        <span>
          <SendOutlined /> 请求数据
        </span>
      ),
      children: jsonPanel(log.request_data)
    },
    {
      key: 'response',
      label: (
        <span>
          <CheckCircleOutlined /> 响应数据
        </span>
      ),
      children: jsonPanel(log.response_data)
    }
  ]

  return (
    <Card variant="borderless" className={styles.card}>
      <div className={styles.header}>
        <div className={styles.basicInfo}>
          <Space size="large" wrap>
            <span className={styles.requestId}>
              <TagOutlined /> {log.request_id}
            </span>
            <Typography.Text type="secondary">
              <ClockCircleOutlined /> {formatDateTime(log.created_at)}
            </Typography.Text>
            {showUserInfo && (
              <Typography.Text type="secondary">
                <UserOutlined /> 用户{log.user_id}
              </Typography.Text>
            )}
          </Space>
          {hasTokenInfo && (
            <Space size="middle" wrap>
              <span>
                {log.provider && <Tag color={providerColor(log.provider)}>{log.provider.toUpperCase()}</Tag>}
                {log.model && <span className={styles.model}>{log.model}</span>}
              </span>
              {!!log.total_tokens && (
                <span>
                  <CloudOutlined /> {log.total_tokens} tokens
                </span>
              )}
              {!!log.total_cost && (
                <span>
                  <DollarOutlined /> ${log.total_cost.toFixed(6)}
                </span>
              )}
              {log.purpose && (
                <span>
                  <TagOutlined /> <Tag>{log.purpose}</Tag>
                </span>
              )}
            </Space>
          )}
        </div>
        <Button size="small" type="text" title="复制请求ID" aria-label="复制请求ID" onClick={copyRequestId}>
          <CopyOutlined />
        </Button>
      </div>
      <Collapse size="small" items={sections} />
    </Card>
  )
}
